// src/utils/tokenStorage.js
import { logDebug } from '../logging/loggingService';

const ACCESS_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'authRefreshToken';

function safeRemove(storage, key) {
    if (!storage || typeof storage.removeItem !== 'function') {
        return;
    }
    try {
        storage.removeItem(key);
    } catch {}
}

function safeSet(storage, key, value) {
    if (!storage || typeof storage.setItem !== 'function') {
        return;
    }
    try {
        storage.setItem(key, value);
    } catch {}
}

function safeGet(storage, key) {
    if (!storage || typeof storage.getItem !== 'function') {
        return null;
    }
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

export function getStoredAccessToken() {
    if (typeof window === 'undefined') {
        return null;
    }
    return safeGet(window.localStorage, ACCESS_TOKEN_KEY) || safeGet(window.sessionStorage, ACCESS_TOKEN_KEY) || null;
}

export function getStoredRefreshToken() {
    if (typeof window === 'undefined') {
        return null;
    }
    return safeGet(window.localStorage, REFRESH_TOKEN_KEY) || safeGet(window.sessionStorage, REFRESH_TOKEN_KEY) || null;
}

function resolvePrimaryStorage(remember) {
    if (typeof window === 'undefined') {
        return null;
    }

    if (remember === true) {
        return window.localStorage ?? null;
    }
    if (remember === false) {
        return window.sessionStorage ?? null;
    }

    if (window.localStorage && (safeGet(window.localStorage, ACCESS_TOKEN_KEY) || safeGet(window.localStorage, REFRESH_TOKEN_KEY))) {
        return window.localStorage;
    }
    if (window.sessionStorage && (safeGet(window.sessionStorage, ACCESS_TOKEN_KEY) || safeGet(window.sessionStorage, REFRESH_TOKEN_KEY))) {
        return window.sessionStorage;
    }

    return window.localStorage ?? window.sessionStorage ?? null;
}

function getSecondaryStorage(primary) {
    if (typeof window === 'undefined') {
        return null;
    }
    if (!primary) {
        return null;
    }
    if (primary === window.localStorage) {
        return window.sessionStorage ?? null;
    }
    if (primary === window.sessionStorage) {
        return window.localStorage ?? null;
    }
    return null;
}

export function storeTokens({ accessToken, refreshToken, remember } = {}) {
    if (typeof window === 'undefined') {
        return;
    }

    const primary = resolvePrimaryStorage(remember);
    const secondary = getSecondaryStorage(primary);

    if (accessToken) {
        safeSet(primary, ACCESS_TOKEN_KEY, accessToken);
    } else {
        safeRemove(primary, ACCESS_TOKEN_KEY);
    }

    if (secondary) {
        safeRemove(secondary, ACCESS_TOKEN_KEY);
    }

    if (refreshToken) {
        safeSet(primary, REFRESH_TOKEN_KEY, refreshToken);
    } else {
        safeRemove(primary, REFRESH_TOKEN_KEY);
    }

    if (secondary) {
        safeRemove(secondary, REFRESH_TOKEN_KEY);
    }
}

export function clearStoredTokens() {
    if (typeof window === 'undefined') {
        return;
    }
    safeRemove(window.localStorage, ACCESS_TOKEN_KEY);
    safeRemove(window.sessionStorage, ACCESS_TOKEN_KEY);
    safeRemove(window.localStorage, REFRESH_TOKEN_KEY);
    safeRemove(window.sessionStorage, REFRESH_TOKEN_KEY);
}

function decodeBase64Url(input) {
    if (!input) {
        return null;
    }
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const normalized = base64 + padding;

    try {
        if (typeof window !== 'undefined' && typeof window.atob === 'function') {
            return window.atob(normalized);
        }
        return Buffer.from(normalized, 'base64').toString('utf-8');
    } catch (error) {
        logDebug('Failed to decode base64url token payload', {
            component: 'tokenStorage',
            error,
        });
        return null;
    }
}

export function decodeJwtClaims(token) {
    if (!token || typeof token !== 'string') {
        return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
        return null;
    }

    const payload = decodeBase64Url(parts[1]);
    if (!payload) {
        return null;
    }

    try {
        return JSON.parse(payload);
    } catch (error) {
        logDebug('Failed to parse JWT payload', {
            component: 'tokenStorage',
            error,
        });
        return null;
    }
}

export function getTokenExpiration(token = getStoredAccessToken()) {
    const claims = decodeJwtClaims(token);
    if (!claims) {
        return null;
    }

    const { exp } = claims;
    if (typeof exp !== 'number') {
        return null;
    }

    return exp * 1000;
}
